import React, { useCallback, useEffect, useState } from 'react';

import { DatasourceModel, WidgetTableColumnsData, WidgetTableColumn } from 'jamespot-user-api';

import { useIntl } from 'react-intl';

import { JRCButton } from 'components/JRCButton/JRCButton';
import { JRCModalLayout } from 'components/JRCModal/JRCModalLayout';
import { JRCModalContent } from 'components/JRCModal/JRCModalContent';

import { WidgetDatasourceModelView } from '../../components/WidgetDatasourceModelView';
import { ExcelEditorObject } from './ExcelEditorObject';

import { ExcelEditorModelColumn } from './ExcelEditorModelColumn';
import { SUPPORTED_DISPLAY_TYPE, SUPPORTED_SORTABLE_TYPE } from '../../const';

import { useEditorContext } from 'components/Widgets/context/hooks';
import { ExcelEditorBrokenModel } from './ExcelEditorBrokenModel';
import { WidgetExcelDatasourceContext } from '../../JRCWidgetExcelDatasourceTableEditor';

export const ExcelEditorModel = ({
    uri,
    tableColumnsData,
    model,
}: {
    uri: string;
    tableColumnsData: WidgetTableColumnsData;
    model: DatasourceModel;
}) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [excelModel, setExcelModel] = useState<DatasourceModel>(model);
    const [excelHeaders, setExcelHeaders] = useState<string[] | undefined>(undefined);
    const [isHeadersBroken, setIsHeadersBroken] = useState<boolean>(false);

    const { onChange, handlers } = useEditorContext<WidgetExcelDatasourceContext>();

    const reloadView = useCallback(() => {
        onChange({ uri: undefined });
        setTimeout(() => {
            onChange({ uri });
        });
    }, [uri, onChange]);

    const onHandleUpdateModel = useCallback(() => {
        if (excelModel) {
            handlers.handleStart();
            handlers.jamespot.datasource
                .updateModel(uri, excelModel)
                .then(() => {
                    setIsOpen(false);
                    reloadView();
                })
                .catch(handlers.handleCatch)
                .finally(handlers.handleFinally);
        }
    }, [uri, excelModel, handlers, reloadView]);

    useEffect(() => {
        if (!uri) {
            return;
        }

        handlers.handleStart();
        handlers.jamespot.datasource
            .getExcelDatasourceHeaders(uri)
            .then((response) => {
                setExcelHeaders(response.result);
                setIsHeadersBroken(!!response.result.filter((el) => !el || el.length === 0).length);
            })
            .catch(handlers.handleCatch)
            .finally(handlers.handleFinally);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uri]);

    const handleUpdateModel = useCallback(
        (model: WidgetTableColumn[]) => {
            handlers.handleStart();
            handlers.jamespot.datasource
                .updateModel(uri, model)
                .then(() => {
                    onChange({ uri: undefined });
                    setTimeout(() => {
                        onChange({
                            tableColumnsData: model.map((el) => {
                                const data = tableColumnsData.find((data) => data.name === el.name);
                                if (data) {
                                    return { name: el.name, ...data };
                                }
                                return {
                                    name: el.name,
                                    isVisible: SUPPORTED_DISPLAY_TYPE.includes(el.type),
                                };
                            }),
                            uri,
                        });
                    });
                })
                .catch(handlers.handleCatch)
                .finally(handlers.handleFinally);
        },
        [handlers, onChange, tableColumnsData, uri],
    );

    const onHandleCancel = () => {
        onChange({
            tableColumnsData: [],
            uri: undefined,
        });
    };

    return (
        <>
            {isHeadersBroken && <ExcelEditorBrokenModel />}
            {uri && <ExcelEditorObject uri={uri} onHandleCancel={onHandleCancel} />}
            {excelModel && !isHeadersBroken && (
                <>
                    <WidgetDatasourceModelView
                        model={excelModel}
                        excelHeaders={excelHeaders}
                        tableColumnsData={tableColumnsData}
                        onChange={onChange}
                        onUpdateModel={handleUpdateModel}
                    />
                    <JRCButton
                        onClick={() => setIsOpen(true)}
                        variant={'outlined'}
                        minWidth={'100%'}
                        disabled={isHeadersBroken}
                        noMargin>
                        {intl.formatMessage({ id: 'WIDGET_Datasource_Excel_Edit_Model' })}
                    </JRCButton>
                </>
            )}
            {excelModel && isOpen && !isHeadersBroken && (
                <JRCModalLayout enableClickAwayCloseModal={true} open={isOpen} closeHandler={() => setIsOpen(false)}>
                    <JRCModalContent
                        overflow={'scroll'}
                        title={intl.formatMessage({ id: 'WIDGET_Datasource_Excel_Edit_Model' })}>
                        <>
                            {excelModel.map((column) => {
                                return (
                                    <ExcelEditorModelColumn
                                        key={column.name}
                                        column={column}
                                        onChange={(data) => {
                                            setExcelModel(
                                                excelModel.map((col) =>
                                                    col.name === data.name
                                                        ? {
                                                              ...col,
                                                              ...data,
                                                              sortable: SUPPORTED_SORTABLE_TYPE.includes(data.type),
                                                          }
                                                        : col,
                                                ),
                                            );
                                        }}
                                    />
                                );
                            })}
                            <JRCButton onClick={onHandleUpdateModel} variant={'outlined'} minWidth={'100%'}>
                                {intl.formatMessage({ id: 'GLOBAL_Confirm' })}
                            </JRCButton>
                        </>
                    </JRCModalContent>
                </JRCModalLayout>
            )}
        </>
    );
};
